import { inject, Injectable } from '@angular/core';
import { VARIABLES } from '@environments/environment.variables';
import { IndexedDbService } from '@shared/services';
import { DataCache } from '@shared/services/data/models';
import { DataTableEnum } from '@shared/services/indexdb/enum';
import { LogsService } from '@shared/services/logs';
import { EMPTY, map, Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class DataService {
  private readonly BUILD_TIMESTAMP = VARIABLES.buildTimestamp;
  private readonly indexDbService = inject(IndexedDbService);
  private readonly logs = inject(LogsService);

  get$<T>(key: string, storeName: keyof typeof DataTableEnum = 'STORAGE'): Observable<T | null> {
    return this.indexDbService.getData(storeName, key).pipe(
      map((cache: unknown) => {
        const dataCache = cache as DataCache<T>;
        if (dataCache && dataCache?.buildTimestamp !== this.BUILD_TIMESTAMP) {
          this.dropInstance().subscribe();
          return null;
        }
        if (dataCache?.expiredAt === 0 || (dataCache?.expiredAt > 0 && dataCache?.expiredAt >= Date.now())) {
          this.logs.trace('DataService', 'Get Data', { key, storeName });
          return dataCache.data;
        }
        if (cache !== undefined) {
          this.remove(key, storeName).subscribe();
        }
        return null;
      }),
      catchError((err) => {
        this.logs.error('DataService', 'Error retrieving data', {
          key,
          storeName,
          error: err as unknown,
        });
        return of(null);
      })
    );
  }

  set<T>(
    key: string,
    value: T,
    expirationTime = 0,
    storeName: keyof typeof DataTableEnum = 'STORAGE'
  ): Observable<void> {
    return this.indexDbService.addOrUpdateData(storeName, key, new DataCache(value, expirationTime)).pipe(
      tap(() => {
        this.logs.trace('DataService', 'Set Data', { key, expirationTime, storeName });
      }),
      catchError((err) => {
        this.logs.error('DataService', 'Error injecting data', {
          key,
          storeName,
          expirationTime,
          error: err as unknown,
        });
        return EMPTY;
      })
    );
  }

  remove(key: string, storeName: keyof typeof DataTableEnum = 'STORAGE'): Observable<void> {
    return this.indexDbService.deleteKey(storeName, key).pipe(
      tap(() => {
        this.logs.trace('DataService', 'Remove Key', { key, storeName });
      }),
      catchError((err) => {
        this.logs.error('DataService', 'Error deleting a key', {
          key,
          storeName,
          error: err as unknown,
        });
        return EMPTY;
      })
    );
  }

  clear(storeName: keyof typeof DataTableEnum = 'STORAGE'): Observable<void> {
    return this.indexDbService.clearTable(storeName).pipe(
      tap(() => {
        this.logs.trace('DataService', 'Clear Table', { storeName });
      }),
      catchError((err) => {
        this.logs.error('DataService', 'Error when deleting a table', {
          storeName,
          error: err as unknown,
        });
        return EMPTY;
      })
    );
  }

  listKeys(storeName: keyof typeof DataTableEnum = 'STORAGE'): Observable<string[]> {
    return this.indexDbService.listKeys(storeName).pipe(
      tap((listKeys) => {
        this.logs.trace('DataService', 'List Keys', { storeName, listKeys });
      }),
      catchError((err) => {
        this.logs.error('DataService', 'Error retrieving list of keys', {
          storeName,
          error: err as unknown,
        });
        return EMPTY;
      })
    );
  }

  dropInstance(): Observable<void> {
    return this.indexDbService.deleteDatabase().pipe(
      tap(() => {
        this.logs.trace('DataService', 'Delete Database');
      }),
      catchError((err) => {
        this.logs.error('DataService', 'Database deletion error', {
          error: err as unknown,
        });
        return EMPTY;
      })
    );
  }
}
